
import { mountThemeFeature } from './editor-lifecycle.js';

mountThemeFeature(lifecycle => {
const { IntersectionObserver, ResizeObserver, setTimeout, setInterval, requestAnimationFrame } = lifecycle;
/* ============================================================
   SKIN BEAUTIFUL MD — halftone particle hero (/science/)
   The thesis, drawn: free particles, given the right conditions,
   organize into living form. One particle per grid cell samples
   the REAL product cutouts (public/product/*-hd.png — client's
   own packshots, background segmented away, never an AI render),
   rests as molecular clusters (electron-cloud density, Brownian
   jiggle, slow swirl) and assembles into an ink halftone of each
   formula in an endless loop. Direction locked 2026-08-27:
   pure dots, no photo/typeset resolve — the sharpened original.
   No library. Honors prefers-reduced-motion (static, hard cuts).
   ============================================================ */
// Shopify port: product urls come from the config; image urls ride the page's own asset base.
const CFG = JSON.parse(document.getElementById('sbmd-config').textContent);
const ASSET_BASE = (document.querySelector('link[href*="tokens.css"]')?.href || '').split('tokens.css')[0];
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const ease = t => 1 - (1 - t) ** 3;                       /* easeOutCubic — house entrance curve */
const clamp01 = t => Math.min(1, Math.max(0, t));
const frac = x => x - Math.floor(x);

const INK = '35,33,32', INDIGO = '42,42,100';             /* --color-black / --color-indigo */
const T = { assemble: 1900, delayMax: 650, hold: 3400, disperse: 1200 };
const SEG = T.assemble + T.delayMax + T.hold + T.disperse;

const PRODUCTS = [
  { src: ASSET_BASE + 'product-ageless-hd.png', name: 'Day & Night Ageless', hook: 'Smooth and restore.', href: CFG.products.ageless.url },
  { src: ASSET_BASE + 'product-citrus-hd.png',  name: 'Citrus Stem Cell Miracle', hook: 'Brighten and firm.', href: CFG.products.citrus.url },
  { src: ASSET_BASE + 'product-deep-hd.png',    name: 'Deep Moisturizer', hook: 'Hydrate and plump.', href: CFG.products.deep.url },
];

const canvas = document.querySelector('[data-halftone]');
if (canvas) boot();

async function boot() {
  const ctx = canvas.getContext('2d');
  const caption = document.querySelector('[data-halftone-caption]');
  const capLink = caption?.querySelector('a');
  const capName = caption?.querySelector('[data-cap-name]');
  const capHook = caption?.querySelector('[data-cap-hook]');

  let imgs;
  try {
    imgs = await Promise.all(PRODUCTS.map(p => new Promise((res, rej) => {
      const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = p.src;
    })));
  } catch { return; }                                     /* no images, no hero — page still works */

  let size, cell, cols, pool = [], clusters = [];
  let cur = 0, t0 = performance.now(), pausedAt = 0, visible = true, raf = 0, capOn = false;

  /* ---------- sample one cutout into an n×n brightness grid ---------- */
  function sample(img) {
    const off = document.createElement('canvas');
    off.width = off.height = cols;
    const o = off.getContext('2d', { willReadFrequently: true });
    const box = cols * 0.86;                              /* 7% breathing room each side */
    const s = Math.min(box / img.naturalWidth, box / img.naturalHeight);
    const w = img.naturalWidth * s, h = img.naturalHeight * s;
    o.drawImage(img, (cols - w) / 2, (cols - h) / 2, w, h);
    const d = o.getImageData(0, 0, cols, cols).data;
    const out = new Float32Array(cols * cols);
    for (let i = 0; i < out.length; i++) {
      const a = d[i * 4 + 3] / 255;
      if (a < 0.62) continue;                             /* cuts the cutouts' soft edge fringe */
      const lum = (0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2]) / 255;
      const edge = Math.min(1, (a - 0.62) / 0.3);         /* soft ramp at the silhouette */
      out[i] = (0.3 + (1 - lum) ** 1.15 * 0.7) * edge;    /* presence floor + midtone-weighted gain */
    }
    return out;
  }

  /* ---------- molecular clusters — where free matter rests ---------- */
  function seedClusters() {
    const n = 12 + Math.floor(Math.random() * 4);
    clusters = Array.from({ length: n }, () => ({
      x: size * (0.08 + Math.random() * 0.84),
      y: size * (0.08 + Math.random() * 0.84),
      r: size * (0.045 + Math.random() * 0.075),          /* cloud radius */
      s1: Math.random() * 6.283, s2: Math.random() * 6.283,
      dir: Math.random() < 0.5 ? -1 : 1,
      spin: 0.00005 + Math.random() * 0.00008,            /* rad/ms — very slow swirl */
    }));
  }
  function assign(p) {                                    /* deterministic per particle */
    p.cl = Math.floor(frac(p.seed * 9973) * clusters.length);
    p.offR = Math.pow(frac(p.seed * 6151), 0.62);         /* denser toward the nucleus */
    p.offA = frac(p.seed * 3571) * 6.283;
    p.freeR = frac(p.seed * 7919) < 0.38 ? 0              /* ~38% rest as invisible dark matter */
      : cell * (0.11 + frac(p.seed * 331) * 0.12) * (1.35 - 0.55 * p.offR);
  }
  function freePos(p, now) {                              /* living position of a resting particle */
    const c = clusters[p.cl];
    const dx = Math.sin(now * 0.00021 + c.s1) * size * 0.028;
    const dy = Math.cos(now * 0.00017 + c.s2) * size * 0.028;
    const a = p.offA + now * c.spin * c.dir;
    const rad = p.offR * c.r * (1 + 0.07 * Math.sin(now * 0.0009 + p.seed * 6.283));
    const jx = Math.sin(now * 0.0013 + p.seed * 43) * cell * 0.4;   /* Brownian jiggle */
    const jy = Math.cos(now * 0.0011 + p.seed * 71) * cell * 0.4;
    return [c.x + dx + Math.cos(a) * rad + jx, c.y + dy + Math.sin(a) * rad + jy];
  }

  /* ---------- (re)build the particle pool for the current canvas size ---------- */
  function build() {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    size = canvas.clientWidth;
    if (!size) return false;
    canvas.width = canvas.height = Math.round(size * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cols = Math.round(Math.min(132, Math.max(72, size / 4.8)));   /* sharpened ~22% over the spike */
    cell = size / cols;
    const grids = imgs.map(sample);
    seedClusters();
    pool = [];
    const now = performance.now();
    for (let i = 0; i < cols * cols; i++) {
      if (!grids[0][i] && !grids[1][i] && !grids[2][i]) continue;
      const seed = ((i * 2654435761) >>> 0) / 4294967296;
      const p = {
        hx: (i % cols + 0.5) * cell, hy: (Math.floor(i / cols) + 0.5) * cell,
        r: grids.map(g => g[i] * cell * 0.47),            /* target dot radius per product */
        seed, delay: seed * T.delayMax,
        x: 0, y: 0, cx: 0, cy: 0, armed: false,
        indigo: seed > 0.93,                              /* ~7% of particles carry the accent */
      };
      assign(p);
      [p.x, p.y] = freePos(p, now); p.cx = p.x; p.cy = p.y;
      pool.push(p);
    }
    return true;
  }

  /* ---------- the loop ---------- */
  function frame(now) {
    raf = 0;
    const local = now - t0;
    if (local >= SEG) {                                   /* next product: fresh droplets, re-assign matter */
      cur = (cur + 1) % 3; t0 = now;
      seedClusters();
      pool.forEach(p => { p.cx = p.x; p.cy = p.y; p.armed = false; assign(p); });
    }
    draw(now - t0, now);
    syncCaption(now - t0);
    if (visible && !document.hidden) raf = requestAnimationFrame(frame);
  }

  function draw(local, now) {
    ctx.clearRect(0, 0, size, size);
    const paths = [new Path2D(), new Path2D(), new Path2D(), new Path2D()]; /* ink, ink-atom, indigo, indigo-atom */
    const A = T.assemble + T.delayMax, H = A + T.hold;
    for (const p of pool) {
      const target = p.r[cur];                            /* 0 → this particle rests as free matter */
      let x, y, r, organized;
      if (local < A) {                                    /* ASSEMBLE — from wherever it was */
        const t = ease(clamp01((local - p.delay) / T.assemble));
        let tx, ty;
        if (target) { tx = p.hx; ty = p.hy; } else [tx, ty] = freePos(p, now);
        x = p.cx + (tx - p.cx) * t; y = p.cy + (ty - p.cy) * t;
        r = (target ? target : p.freeR) * t + p.freeR * (1 - t) * 0.6;
        organized = !!target;
      } else if (local < H) {                             /* HOLD — form breathes, matter rests */
        if (target) {
          x = p.hx; y = p.hy;
          r = target * (1 + 0.045 * Math.sin(now / 900 + p.seed * 6.283));
          organized = true;
        } else { [x, y] = freePos(p, now); r = p.freeR; organized = false; }
      } else {                                            /* DISPERSE — release into the droplets */
        if (!p.armed) { p.cx = p.x; p.cy = p.y; p.armed = true; }
        const t = ease(clamp01((local - H) / T.disperse));
        const [tx, ty] = freePos(p, now);
        x = p.cx + (tx - p.cx) * t; y = p.cy + (ty - p.cy) * t;
        r = (target || p.freeR) + (p.freeR - (target || p.freeR)) * t;
        organized = false;
      }
      p.x = x; p.y = y;
      if (r < 0.28) continue;
      const path = paths[(p.indigo ? 2 : 0) + (organized ? 0 : 1)];
      path.moveTo(x + r, y);
      path.arc(x, y, r, 0, 6.283);
    }
    ctx.fillStyle = `rgba(${INK},0.92)`;    ctx.fill(paths[0]);
    ctx.fillStyle = `rgba(${INK},0.34)`;    ctx.fill(paths[1]);
    ctx.fillStyle = `rgba(${INDIGO},0.95)`; ctx.fill(paths[2]);
    ctx.fillStyle = `rgba(${INDIGO},0.38)`; ctx.fill(paths[3]);
  }

  function syncCaption(local) {
    const on = local > T.assemble * 0.7 && local < T.assemble + T.delayMax + T.hold + T.disperse * 0.3;
    if (on && !capOn) {
      const P = PRODUCTS[cur];
      if (capName) capName.textContent = P.name;
      if (capHook) capHook.textContent = P.hook;
      if (capLink) capLink.href = P.href;
    }
    if (on !== capOn) { caption?.classList.toggle('is-shown', on); capOn = on; }
  }

  /* ---------- reduced motion: assembled stills among frozen matter, hard cuts ---------- */
  function drawStatic() {
    draw(T.assemble + T.delayMax + T.hold / 2, 4321);
    const P = PRODUCTS[cur];
    if (capName) capName.textContent = P.name;
    if (capHook) capHook.textContent = P.hook;
    if (capLink) capLink.href = P.href;
    caption?.classList.add('is-shown');
  }

  /* ---------- lifecycle ---------- */
  if (!build()) { new ResizeObserver((_, o) => { if (build()) { o.disconnect(); start(); } }).observe(canvas); return; }
  start();

  function start() {
    if (reduced) { drawStatic(); setInterval(() => { cur = (cur + 1) % 3; drawStatic(); }, 5000); return; }
    let rto = 0;
    new ResizeObserver(() => {
      clearTimeout(rto);
      rto = setTimeout(() => { if (Math.abs(canvas.clientWidth - size) > 2) { build(); t0 = performance.now(); } }, 180);
    }).observe(canvas);
    new IntersectionObserver(es => es.forEach(e => {
      visible = e.isIntersecting;
      if (visible && !raf) { t0 += performance.now() - pausedAt; raf = requestAnimationFrame(frame); }
      else if (!visible) pausedAt = performance.now();
    }), { threshold: 0.05 }).observe(canvas);
    lifecycle.listen(document, 'visibilitychange', () => {
      if (document.hidden) pausedAt = performance.now();
      else if (visible && !raf) { t0 += performance.now() - pausedAt; raf = requestAnimationFrame(frame); }
    });
    pausedAt = t0 = performance.now();
    raf = requestAnimationFrame(frame);
  }
}

});
